import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { getRoleFilterInfo } from '../utils/roleFilters';
import { readCache, writeCache } from '../utils/cacheManager';

type CampaignType = 'call' | 'whatsapp' | 'email' | 'linkedin' | 'omni' | 'inbound';

export interface CampaignData {
  totalLeads: number;
  activeLeads: number;
  completedLeads: number;
  engagedLeads: number;
  meetingsBooked: number;
  replyRate: number;
  conversionRate: number;
  contactedLeads: number;
}

export interface CampaignSummary {
  totalLeads: number;
  activeLeads: number;
  meetingsBooked: number;
  replyRate: number;
}

interface CampaignStatsState {
  stats: Record<CampaignType, CampaignData>;
  summary: CampaignSummary;
}

const CAMPAIGN_TYPES: CampaignType[] = ['call', 'whatsapp', 'email', 'linkedin', 'omni', 'inbound'];
const CACHE_KEY = 'campaign_stats_v1';

const EMPTY_DATA: CampaignData = {
  totalLeads: 0, activeLeads: 0, completedLeads: 0, engagedLeads: 0,
  meetingsBooked: 0, replyRate: 0, conversionRate: 0, contactedLeads: 0,
};

const EMPTY_SUMMARY: CampaignSummary = { totalLeads: 0, activeLeads: 0, meetingsBooked: 0, replyRate: 0 };

function buildEmptyStats(): Record<CampaignType, CampaignData> {
  return {
    call: { ...EMPTY_DATA },
    whatsapp: { ...EMPTY_DATA },
    email: { ...EMPTY_DATA },
    linkedin: { ...EMPTY_DATA },
    omni: { ...EMPTY_DATA },
    inbound: { ...EMPTY_DATA },
  };
}

function parseRpcResult(rows: any[]): CampaignStatsState {
  const stats = buildEmptyStats();
  let summary = EMPTY_SUMMARY;

  for (const row of rows) {
    if (row.campaign_type === '_summary') {
      summary = {
        totalLeads: row.total_leads || 0,
        activeLeads: row.active_leads || 0,
        meetingsBooked: row.meetings_booked || 0,
        replyRate: row.reply_rate || 0,
      };
      continue;
    }

    const type = row.campaign_type as CampaignType;
    if (!CAMPAIGN_TYPES.includes(type)) continue;

    stats[type] = {
      totalLeads: row.total_leads || 0,
      activeLeads: row.active_leads || 0,
      completedLeads: row.completed_leads || 0,
      engagedLeads: row.engaged_leads || 0,
      meetingsBooked: row.meetings_booked || 0,
      replyRate: row.reply_rate || 0,
      conversionRate: row.conversion_rate || 0,
      contactedLeads: row.contacted_leads || 0,
    };
  }

  return { stats, summary };
}

export function useCampaignStats() {
  const [state, setState] = useState<CampaignStatsState>(() => {
    const cached = readCache(CACHE_KEY);
    if (cached && cached.length > 0) {
      return parseRpcResult(cached);
    }
    return { stats: buildEmptyStats(), summary: EMPTY_SUMMARY };
  });
  const [isLoading, setIsLoading] = useState(() => {
    const cached = readCache(CACHE_KEY);
    return !cached || cached.length === 0;
  });
  const [error, setError] = useState<string | null>(null);

  const isMountedRef = useRef(true);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const fetchData = useCallback(async (isBackground = false) => {
    const roleInfo = getRoleFilterInfo();
    if (!roleInfo) {
      if (isMountedRef.current) {
        setState({ stats: buildEmptyStats(), summary: EMPTY_SUMMARY });
        setIsLoading(false);
      }
      return;
    }

    if (!isBackground) setIsLoading(true);
    setError(null);

    try {
      const { data, error: rpcError } = await supabase.rpc('get_campaign_stats', {
        p_client_id: roleInfo.clientId,
        p_user_id: roleInfo.isAdmin ? null : roleInfo.userId,
      });

      if (rpcError) throw rpcError;

      const rows = data || [];
      if (isMountedRef.current) {
        setState(parseRpcResult(rows));
        setIsLoading(false);
        writeCache(CACHE_KEY, rows);
      }
    } catch (err) {
      console.error('[useCampaignStats] Error:', err);
      if (isMountedRef.current) {
        const hasCache = state.summary.totalLeads > 0;
        if (!hasCache) {
          setError(err instanceof Error ? err.message : 'Failed to load campaign stats');
        }
        setIsLoading(false);
      }
    }
  }, []);

  const debouncedFetch = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      fetchData(true);
    }, 1000);
  }, [fetchData]);

  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData;
  const debouncedFetchRef = useRef(debouncedFetch);
  debouncedFetchRef.current = debouncedFetch;

  useEffect(() => {
    isMountedRef.current = true;

    const hasCache = readCache(CACHE_KEY) && (readCache(CACHE_KEY) || []).length > 0;
    if (hasCache) {
      fetchDataRef.current(true);
    } else {
      fetchDataRef.current(false);
    }

    const channel = supabase
      .channel(`campaign_stats_refresh_${Math.random()}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'leads_campaigns' }, () => {
        debouncedFetchRef.current();
      })
      .subscribe();

    return () => {
      isMountedRef.current = false;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      supabase.removeChannel(channel);
    };
  }, []);

  return {
    campaignStats: state.stats,
    summary: state.summary,
    isLoading,
    error,
    refetch: () => fetchData(true),
  };
}
